import * as Sentry from "@sentry/angular";
import { Injectable, Injector } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';

import { ToastService } from './toast.service';

import { environment } from '@env/environment';
import { switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/registration';
import { StoreToken } from '../../store/actions';
import { AuthService } from "@auth0/auth0-angular";

@Injectable({
    providedIn: 'root'
})
export class ErrorService {

    constructor(
        private toastService: ToastService,
        private store: Store<AppState>,
        private readonly injector: Injector,
    ) {
    }

    /**
     * Use an {@link Injector} to load the {@link AuthService}. If the AuthService is directly injected during
     * construction it will fail to load because the {@link Auth0Client} is not yet initialized. This is due to
     * this component being loaded before the {@link initializeAuth0Client} method is executed.
     */
    private authService(): AuthService {
        return this.injector.get(AuthService);
    }

    public handleError(error: Error | HttpErrorResponse, ignoreErrorToast: boolean = false): Observable<Error | HttpErrorResponse> {
        if (!environment.prodMode) {
            console.error('[ErrorService] handleError:', { error });
        }

        if (error instanceof HttpErrorResponse) {
            /*
            TODO Can be enabled again once we rework the driver scope verification.
            Must be disabled now to ensure the first REST call (previously to getDepots - driver related) does not log us out in case a non-driver logs in.
            if (error.status === 401) {
                this.authService().logout({
                    returnTo: window.location.origin
                });
            } else */ if (error.status === 403) {
                return this.handleExpirationError(error);
            } else {
                if (!ignoreErrorToast) {
                    this.toastService.generateErrorToast(error);
                    Sentry.withScope(scope => {
                        scope.setTag('marker', 'unexpected HTTP error');
                        scope.setLevel('error');
                        scope.setFingerprint(['REQ', error.url, String(error.status)]);
                        Sentry.captureMessage(error.message, {
                            extra: {
                                status: error.status,
                                statusText: error.statusText,
                                url: error.url,
                                error: error.error,
                            },
                        });
                    });
                }
                return of(error);
            }
        }

        return of(null);
    }

    public handleExpirationError(error: Error | HttpErrorResponse): Observable<Error | HttpErrorResponse> {
        return this.authService().getAccessTokenSilently()
            .pipe(
                switchMap(jwt => {
                    this.store.dispatch(new StoreToken(jwt));
                    return throwError(error);
                })
            );
    }
}
